import { User, UserRole } from '../types/auth';

const BASIC_AUTH_KEY = 'basicAuth';
const LEGACY_ACCESS_TOKEN_KEY = 'accessToken';
const LEGACY_REFRESH_TOKEN_KEY = 'refreshToken';

export const isAuthenticated = (): boolean => {
  const basicAuth = localStorage.getItem(BASIC_AUTH_KEY);
  const hasBasicAuth = !!basicAuth && basicAuth.startsWith('Basic ');

  if (hasBasicAuth) {
    return true;
  }

  // Legacy JWT-only state after migration should be cleared deterministically.
  if (localStorage.getItem(LEGACY_ACCESS_TOKEN_KEY) || localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY)) {
    clearAuthTokens();
  }

  return false;
};

export const getCurrentUser = (): User | null => {
  const userStr = localStorage.getItem('currentUser');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  return null;
};

export const getUserRole = (): UserRole | null => {
  const user = getCurrentUser();
  return user?.role || null;
};

export const isAdmin = (): boolean => {
  return getUserRole() === 'admin';
};

export const clearAuthTokens = (): void => {
  localStorage.removeItem(BASIC_AUTH_KEY);
  localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  localStorage.removeItem('currentUser');
  localStorage.removeItem('adminSelectedTab');
};

export const getBasicAuthHeader = (): string | null => {
  const basicAuth = localStorage.getItem(BASIC_AUTH_KEY);
  if (!basicAuth || !basicAuth.startsWith('Basic ')) {
    return null;
  }
  return basicAuth;
};

export const hasAuthorities = (): boolean => {
  const user = getCurrentUser();
  if (!user || !user.authorities || !Array.isArray(user.authorities)) {
    return false;
  }
  return user.authorities.length > 0;
};

export const hasAuthority = (authority: string): boolean => {
  const user = getCurrentUser();
  if (!user || !user.authorities || !Array.isArray(user.authorities)) {
    console.log(`hasAuthority(${authority}): No user or authorities`, user);
    return false;
  }
  
  // Check exact match
  if (user.authorities.includes(authority)) {
    console.log(`hasAuthority(${authority}): Exact match found`);
    return true;
  }
  
  // Check if user has a wildcard authority that matches the requested authority
  // For example: if user has "offer:agents:*", it matches "offer:agents:*" or "offer:agents:view"
  for (const userAuthority of user.authorities) {
    if (userAuthority.endsWith(':*')) {
      const userAuthorityPrefix = userAuthority.slice(0, -2); // Remove ':*'
      // Check if the requested authority starts with the prefix (e.g., "offer:agents")
      if (authority.startsWith(userAuthorityPrefix + ':')) {
        console.log(`hasAuthority(${authority}): Wildcard match found (${userAuthority})`);
        return true;
      }
      // Also check exact match with wildcard (e.g., "offer:agents:*" === "offer:agents:*")
      if (authority === userAuthorityPrefix || authority === userAuthority) {
        console.log(`hasAuthority(${authority}): Wildcard exact match found (${userAuthority})`);
        return true;
      }
    }
  }
  
  // Check if requested authority is a wildcard and user has any matching authority
  // For example: if checking "offer:agents:*" and user has "offer:agents:view"
  if (authority.endsWith(':*')) {
    const authorityPrefix = authority.slice(0, -2);
    for (const userAuthority of user.authorities) {
      if (userAuthority.startsWith(authorityPrefix + ':')) {
        console.log(`hasAuthority(${authority}): User has matching authority (${userAuthority})`);
        return true;
      }
    }
  }
  
  console.log(`hasAuthority(${authority}): No match. Available:`, user.authorities);
  return false;
};